import { useState } from "react"
import { router } from "@inertiajs/react"
import { cn } from "@/lib/cn"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { AgentAvatar } from "./agent-avatar"
import { AgentStatusBadge } from "./agent-status-badge"
import type { Agent } from "@/types/agent"

type Props = {
  agents: Agent[]
}

// Agents list panel — search input header + dense table body. Clicking
// a row (or pressing Enter/Space on focus) navigates to that agent's
// edit page. The inline Edit button is the explicit affordance.
export function AgentTable({ agents }: Props) {
  const [query, setQuery] = useState("")

  const filtered = query.trim().length === 0
    ? agents
    : agents.filter((a) =>
        a.name.toLowerCase().includes(query.toLowerCase()) ||
        a.role.toLowerCase().includes(query.toLowerCase())
      )

  function open(id: number) {
    router.visit(`/agents/${id}/edit`)
  }

  return (
    <div className="overflow-hidden rounded-card border border-border bg-app-panel">
      {/* Mobile: header stacks (title + helper text on top, search bar
          full-width below). md+: search collapses to a 240px-wide
          right-aligned input alongside the title block. */}
      <header className="flex flex-col gap-3 border-b border-border p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="font-semibold">All Agents</div>
          <div className="text-sm text-app-muted">
            Tap an agent to open its edit page.
          </div>
        </div>
        <Input
          placeholder="Search agents…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="md:w-60"
        />
      </header>

      <div className="divide-y divide-border">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-app-muted">No agents match "{query}".</div>
        )}
        {filtered.map((a) => (
          // Row is a clickable region (not a <button>) so the inline Edit
          // <button> inside doesn't violate the "no nested interactive
          // elements" HTML rule. Keyboard support wired explicitly.
          //
          // Layout:
          //   Mobile  — stack: avatar+name row, meta chips row, edit
          //             button row (the entire row is still tappable;
          //             the explicit Edit button is the visual cue).
          //   md+     — original 5-col grid: identity / status / count
          //             / quality / edit chip.
          <div
            key={a.id}
            role="button"
            tabIndex={0}
            onClick={() => open(a.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                open(a.id)
              }
            }}
            className={cn(
              "flex w-full cursor-pointer flex-col gap-3 p-4 text-left text-sm transition outline-none",
              "md:grid md:grid-cols-[1fr_120px_120px_90px_90px] md:items-center md:gap-4",
              "hover:bg-white/5 focus-visible:bg-white/5 focus-visible:ring-1 focus-visible:ring-brand-red/40",
            )}
          >
            <span className="flex items-center gap-3">
              <AgentAvatar name={a.name} />
              <span className="min-w-0">
                <span className="block truncate font-medium text-white">{a.name}</span>
                <span className="block truncate text-app-muted">{a.role}</span>
              </span>
            </span>
            {/* Mobile: chips wrap in one row.  md+: each lives in its
                own grid column. */}
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1 md:contents">
              <AgentStatusBadge status={a.status} />
              <span className="text-xs text-app-muted md:text-sm md:text-left">
                {a.ticketsHandled.toLocaleString()} tickets
              </span>
              <span className="text-xs font-semibold text-vip-goldSoft md:text-sm md:text-right">
                {a.qualityScore}%
              </span>
            </span>
            <span className="md:text-right">
              <Button
                variant="ghost"
                size="sm"
                className="w-full md:w-auto"
                onClick={(e) => {
                  e.stopPropagation()
                  open(a.id)
                }}
              >
                Edit
              </Button>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
